import { OtpInput } from './otp-input';
import IconsMapping from '~/components/ui/Icons/IconsMapping';

interface OtpVerificationProps {
  email: string;
  otp: string;
  onOtpChange: (value: string) => void;
  state: 'idle' | 'verifying' | 'verified' | 'failed';
  error: string;
  canResend: boolean;
  resendCountdown: number;
  onVerify: (otpValue?: string) => void;
  onResend: () => void;
  onBack: () => void;
}

// Email masking function
const maskEmail = (email: string) => {
  const [localPart, domain] = email.split('@');

  if (!localPart || !domain) {
    return email;
  }

  if (localPart.length <= 2) {
    return email;
  }

  const domainParts = domain.split('.');
  const subdomain = domainParts[1] || domainParts[0] || '';

  return `${localPart.substring(0, 2)}${'*'.repeat(6)}.${subdomain}`;
};

export default function OtpVerification({
  email,
  otp,
  onOtpChange,
  state,
  error,
  canResend,
  resendCountdown,
  onVerify,
  onResend,
  onBack,
}: OtpVerificationProps) {
  return (
    <div className="w-full max-w-md mx-auto font-neue-montreal px-4">
      <div className="flex flex-col gap-4 sm:gap-4">
        <div className="flex justify-center">
          <img src="/images/logo-black.svg" alt="Dualite" className="w-12 sm:w-10 mb-4" loading="eager" />
        </div>
        {/* Header */}
        <div className="flex flex-col gap-2">
          <h1 className="text-[#1D1D1D] text-3xl sm:text-2xl lg:text-3xl xl:text-4xl font-medium leading-[1.2em] mb-2 text-center">
            Verify your email
          </h1>

          <div className="text-[#4D5763] text-[14px] font-normal leading-[1.2em] text-center  ">
            <p>We&apos;ve sent a 6-digit code to {maskEmail(email)}</p>
            <p>Enter it below to continue.</p>
          </div>
        </div>

        {/* Form */}
        <div className="flex flex-col gap-1 items-center">
          <div className="w-fit flex flex-col items-center">
            {/* OTP Input Component */}
            <div className="flex justify-center py-4">
              <OtpInput
                value={otp}
                onChange={(value: string) => {
                  onOtpChange(value);
                }}
                maxLength={6}
                disabled={state === 'verifying'}
                onComplete={(value) => onVerify(value)}
                hasError={state === 'failed'}
              />
            </div>

            {/* Status Messages */}
            {state === 'failed' && error && (
              <div className="w-full text-center py-2">
                <p className="text-[#FF796E] text-sm font-normal leading-[1.2em]">{error}</p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-4 flex flex-col items-center w-full">
              <button
                type="button"
                onClick={() => onVerify()}
                disabled={!otp || otp.length !== 6 || state === 'verifying'}
                className={`w-full rounded-xl py-3 sm:py-2.5 px-4 shadow-sm transition-colors min-h-[48px] sm:min-h-auto ${
                  otp && otp.length === 6 && state !== 'verifying'
                    ? 'bg-white border border-[#222] text-[#222222] hover:bg-[#F3F3F3] '
                    : 'bg-[#F3F3F3]  text-[#333333]/50 cursor-not-allowed border border-[#222]/5'
                }`}
              >
                {state === 'verifying' ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-[#333333] border-t-[#CCCCCC] rounded-full animate-spin"></div>
                    <span className="text-base leading-[1.5em] tracking-[0.02em]">Verifying...</span>
                  </div>
                ) : state === 'verified' ? (
                  <span className="text-base leading-[1.5em] tracking-[0.02em]">Verified</span>
                ) : (
                  <span className="text-base leading-[1.5em] tracking-[0.02em]">Verify &amp; Continue</span>
                )}
              </button>

              {/* Bottom Navigation */}
              <div className="text-center text-[rgba(255,255,255,0.5)] text-sm font-normal leading-[1.2em] flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 w-full">
                <button
                  type="button"
                  className="text-[#4D5763] text-[13px] flex items-center justify-center sm:justify-start gap-1 cursor-pointer min-h-[44px] sm:min-h-auto"
                  onClick={onBack}
                >
                  <div className="i-ph:arrow-left w-4 h-4" />
                  <span>Use a different Email</span>
                </button>

                <button
                  type="button"
                  className={`text-[#4D5763] text-[13px] text-center sm:text-right min-h-[44px] sm:min-h-auto flex items-center justify-center sm:justify-end gap-1 ${
                    canResend ? 'cursor-pointer text-[#4D5763] hover:text-[#4D5763]' : 'opacity-50 cursor-not-allowed'
                  }`}
                  onClick={canResend ? onResend : undefined}
                  disabled={!canResend}
                >
                  {IconsMapping.refresh}
                  <span>Resend code {resendCountdown > 0 ? `in ${resendCountdown}s` : ''}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
